import {
  CheckboxWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Typography
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { idempotencyKey, t } from './shared'
import useStyles from './styles/SubscriptionForm.styles'
import { ResourceRow } from './types'

const CHANNELS = ['IN_APP', 'SMS', 'EMAIL'] as const

/** From the BFF schema: enum NotificationBindingKind { USER RECIPIENT_CLASS ENDPOINT } */
const BINDING_KINDS = ['USER', 'RECIPIENT_CLASS', 'ENDPOINT'] as const

interface Binding {
  id: string
  kind: string
  value: string
  active: boolean
}

interface SubscriptionFormProps {
  initial?: ResourceRow
  saving: boolean
  /**
   * True when the call site binds `entity` for a scoped operator
   * (`bindNotificationScope` in AdminPanel). A global operator has no implied
   * scope, so the form must ask for one.
   */
  scopeBound: boolean
  onSubmit: (subscription: ResourceRow) => void
}

/**
 * A real form for notification subscriptions, replacing the JSON textarea (§6).
 *
 * The last and hardest of the three, because `bindings` is
 * `[NotificationBindingInput!]!` — NON-NULL. A form that omitted it could not
 * leave it alone: it would have to send `[]`, which wipes a subscription's
 * recipients on every save. So the bindings editor is not an optional extra
 * here, it is what makes the form safe to use at all.
 */
export default function SubscriptionForm({
  initial,
  saving,
  scopeBound,
  onSubmit
}: SubscriptionFormProps) {
  const classes = useStyles()
  // Lazy initializer, matching TemplateForm: the fallback generates an id, and
  // the eager form would run that generator on every render and discard the
  // result.
  const [draft, setDraft] = useState<ResourceRow>(
    () =>
      initial ?? {
        // `id` is ID! on the input, required even when creating. The screen
        // already generates opaque ids for idempotency keys; reuse that rather
        // than inventing a second generator.
        id: idempotencyKey(),
        entity: { type: 'TENANT', code: '' },
        categoryKey: '',
        channel: 'IN_APP',
        active: true,
        bindings: []
      }
  )

  const { data: categoryData } = useQuery(
    notificationGraphql.NOTIFICATION_ADMIN_CATEGORIES_QUERY,
    { variables: { pageSize: 100 } }
  )
  const categories: ResourceRow[] =
    categoryData?.notificationAdminCategories?.categories ?? []

  const set = (field: string, value: unknown) =>
    setDraft((current) => ({ ...current, [field]: value }))

  const entity = (draft.entity ?? {}) as { type?: string; code?: string }
  const bindings = (draft.bindings as Binding[]) ?? []

  const setBinding = (index: number, field: string, value: unknown) =>
    set(
      'bindings',
      bindings.map((binding, position) =>
        position === index ? { ...binding, [field]: value } : binding
      )
    )

  return (
    <Box mt={2}>
      <SectionTitle title={t('Create or update subscription')} />

      <div className={classes.grid}>
        <SelectWithLabel
          id="subscription-category"
          name="subscription-category"
          label={t('Category')}
          value={String(draft.categoryKey ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('categoryKey', event.target.value)
          }>
          {categories.map((category) => (
            <MenuItem key={String(category.key)} value={String(category.key)}>
              {String(category.key)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <SelectWithLabel
          id="subscription-channel"
          name="subscription-channel"
          label={t('Channel')}
          value={String(draft.channel ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('channel', event.target.value)
          }>
          {CHANNELS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </SelectWithLabel>
      </div>

      {/* Only a GLOBAL operator supplies the entity. For a scoped one the call
          site binds it (bindNotificationScope), and offering the field would
          let them type a scope they cannot write to. */}
      {!scopeBound && (
        <div className={classes.grid}>
          <InputWithLabel
            id="subscription-entity-type"
            name="subscription-entity-type"
            label={t('Entity type')}
            value={entity.type ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              set('entity', { ...entity, type: event.currentTarget.value })
            }
          />
          <InputWithLabel
            id="subscription-entity-code"
            name="subscription-entity-code"
            label={t('Entity code')}
            value={entity.code ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              set('entity', { ...entity, code: event.currentTarget.value })
            }
          />
        </div>
      )}

      <Box mt={2}>
        <Typography variant="subtitle2">{t('Recipients')}</Typography>
        {/* bindings is non-null on the input, so this list is always sent.
            An empty one is a legitimate state — a subscription with no
            recipients — but it must be a CHOICE the operator sees, not a
            silent consequence of the editor not offering the field. */}
        {bindings.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            {t('No recipients. This subscription will notify nobody.')}
          </Typography>
        )}
        {bindings.map((binding, index) => (
          <div className={classes.bindingRow} key={binding.id}>
            <SelectWithLabel
              id={`binding-kind-${index}`}
              name={`binding-kind-${index}`}
              label={t('Kind')}
              value={binding.kind}
              onChange={(event: { target: { value: unknown } }) =>
                setBinding(index, 'kind', event.target.value)
              }>
              {BINDING_KINDS.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(option)}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <InputWithLabel
              id={`binding-value-${index}`}
              name={`binding-value-${index}`}
              label={t('Value')}
              value={binding.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBinding(index, 'value', event.currentTarget.value)
              }
            />
            <CheckboxWithLabel
              label={t('Active')}
              noHelperText
              checked={binding.active}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBinding(index, 'active', event.currentTarget.checked)
              }
            />
            <IconButton
              aria-label={t('Remove recipient {position}', {
                position: index + 1
              })}
              onClick={() =>
                set(
                  'bindings',
                  bindings.filter((_row, position) => position !== index)
                )
              }>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            set('bindings', [
              ...bindings,
              { id: idempotencyKey(), kind: 'USER', value: '', active: true }
            ])
          }>
          {t('Add recipient')}
        </Button>
      </Box>

      <div className={classes.switches}>
        <CheckboxWithLabel
          label={t('Subscription active')}
          noHelperText
          checked={Boolean(draft.active)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('active', event.currentTarget.checked)
          }
        />
      </div>

      <Button
        variant="contained"
        color="primary"
        disabled={saving}
        className={classes.submit}
        onClick={() => onSubmit(draft)}>
        {saving ? t('Saving…') : t('Save subscription')}
      </Button>
    </Box>
  )
}
